import { BACKEND_URL } from './config';
import { getAccessToken } from './session';
import { drainSseStream, assertSseResponse } from './sse';
import { dispatchChatSseEvent } from './chat-sse-dispatch';
import type { ChatAttachment } from './chat-api';

export interface StudentChatWidget {
  name: 'render_widget';
  toolCallId: string;
  html: string;
  title: string | null;
  args: Record<string, unknown>;
}

export interface StudentChatCallbacks {
  onMeta: (meta: { turnsLeft: number }) => void;
  onDelta: (text: string) => void;
  /** Un outil démarre — sert à dire à l'enfant que Naïa consulte le programme. */
  onTool: (name: string) => void;
  onWidget: (widget: StudentChatWidget) => void;
  /** Le serveur rétracte le texte déjà streamé : le tampon doit repartir à vide. */
  onRetract: () => void;
  onDone: (text: string) => void;
  onError: (message: string) => void;
  onSlow?: () => void;
  onActive?: () => void;
}

export class StudentChatDailyCapError extends Error {
  constructor(readonly cap: number) {
    super(`daily_cap_reached:${cap}`);
  }
}

export class StudentChatDisabledError extends Error {}

/** Un tour du chat libre de l'élève, streamé (SSE), mené sous le compte parent. */
export async function streamStudentChatTurn(
  studentId: string,
  threadId: string,
  userMessage: string,
  cb: StudentChatCallbacks,
  signal?: AbortSignal,
  attachments?: ChatAttachment[] | null,
  clientSendId?: string,
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}/student-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token ?? ''}`,
    },
    body: JSON.stringify({
      studentId,
      threadId,
      userMessage,
      ...(clientSendId ? { clientSendId } : {}),
      ...(attachments && attachments.length ? { attachments } : {}),
    }),
    signal,
  });

  if (res.status === 429) {
    const body = (await res.json().catch(() => ({}))) as { cap?: number };
    throw new StudentChatDailyCapError(body.cap ?? 0);
  }
  if (res.status === 403) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (body.error === 'student_chat_disabled') throw new StudentChatDisabledError();
  }
  await assertSseResponse(res, 'student-chat');

  await drainSseStream(res.body!, ({ event, data }) => {
    dispatchChatSseEvent(event, data, {
      onMeta: (d) => cb.onMeta(d as { turnsLeft: number }),
      onDelta: (d) => cb.onDelta(d.text ?? ''),
      onTool: (d) => cb.onTool(d.name ?? ''),
      onWidget: (d) => cb.onWidget(d as unknown as StudentChatWidget),
      onRetract: () => cb.onRetract(),
      onDone: (d) => cb.onDone(d.text ?? ''),
      onError: (d) => cb.onError(d.message ?? 'Erreur'),
    });
  }, undefined, { onSlow: cb.onSlow, onActive: cb.onActive });
}

/** Noms d'outils curriculum, pour l'indicateur « consulte le programme ». */
const CURRICULUM_TOOLS = new Set([
  'list_curriculum_referential',
  'find_programme',
  'get_programme_outline',
  'query_program_items',
  'get_progression',
]);

/** Ce que l'enfant lit pendant qu'un outil tourne. Null = rien à afficher. */
export function toolStatusLabel(name: string): string | null {
  if (CURRICULUM_TOOLS.has(name)) return 'Naïa regarde le programme officiel…';
  if (name === 'render_widget' || name === 'get_widget_recipe') return 'Naïa prépare un schéma…';
  return null;
}
